import React, { useState } from 'react';
import { contactOptions as baseOptions } from '../data/mockData';
import { useLanguage } from '../i18n/LanguageContext';
import { Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const ContactForm = () => {
  const { t } = useLanguage();
  const { toast } = useToast();

  const optionTranslations = {
    vertical: { label: t('contact.options.vertical'), price: t('contact.options.verticalPrice') },
    youtube: { label: t('contact.options.youtube'), price: t('contact.options.youtubePrice') },
    monthly: { label: t('contact.options.monthly'), price: t('contact.options.monthlyPrice') },
    custom: { label: t('contact.options.custom'), price: '-' },
  };
  const subOptionTranslations = {
    '30seg': t('contact.options.duration30'),
    '60seg': t('contact.options.duration60'),
    '180seg': t('contact.options.duration180'),
  };
  const contactOptions = baseOptions.map(opt => ({
    ...opt,
    label: optionTranslations[opt.value]?.label || opt.label,
    price: optionTranslations[opt.value]?.price || opt.price,
    subOptions: opt.subOptions?.map(sub => ({
      ...sub,
      label: subOptionTranslations[sub.value] || sub.label,
    })),
  }));

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: 'vertical',
    projectSubOption: '',
    customDetails: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        let detail = 'Request failed';
        try {
          const body = await response.json();
          if (body.error) detail = body.error;
        } catch (_) {}
        throw new Error(detail);
      }

      toast({
        title: t('contact.toastTitle'),
        description: t('contact.toastDescription'),
      });

      setFormData({
        name: '',
        email: '',
        projectType: 'vertical',
        projectSubOption: '',
        customDetails: '',
        message: ''
      });
    } catch (error) {
      let detail = 'No se pudo enviar el mensaje. Intenta nuevamente.';
      if (error.message && error.message.includes(':')) {
        detail = error.message.split(':').slice(1).join(':').trim();
      }
      toast({
        variant: 'destructive',
        title: 'Error',
        description: detail,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedOption = contactOptions.find(opt => opt.value === formData.projectType);

  return (
    <section id="contacto" className="py-24 md:py-40 relative overflow-hidden bg-white">
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(0,0,0,0.04),transparent_36%),radial-gradient(circle_at_85%_32%,rgba(0,0,0,0.035),transparent_42%),radial-gradient(circle_at_60%_72%,rgba(0,0,0,0.03),transparent_40%)]" />
      </div>

      <div className="container mx-auto px-6 max-w-2xl relative z-10">
        <div className="mb-16 text-center">
          <p
            className="inline-block rounded-full border border-black/15 bg-black/5 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-black/60 mb-6"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('contact.badge')}
          </p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05] mb-5 text-black">
            {t('contact.title')}
          </h2>
          <p className="text-lg md:text-xl text-black/55 leading-[1.5] max-w-md mx-auto">
            {t('contact.description')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-black/55 ml-1">{t('contact.nameLabel')}</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('contact.namePlaceholder')}
                required
                className="w-full h-12 px-4 bg-white border border-black/10 rounded-xl text-[15px] text-black placeholder:text-black/35 shadow-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/5 focus-visible:ring-offset-0 focus-visible:border-black/40 transition-all duration-200"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-semibold text-black/55 ml-1">{t('contact.emailLabel')}</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('contact.emailPlaceholder')}
                required
                className="w-full h-12 px-4 bg-white border border-black/10 rounded-xl text-[15px] text-black placeholder:text-black/35 shadow-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/5 focus-visible:ring-offset-0 focus-visible:border-black/40 transition-all duration-200"
              />
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-xs font-semibold text-black/55 ml-1">{t('contact.typeLabel')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {contactOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    projectType: option.value,
                    projectSubOption: '',
                    customDetails: ''
                  })}
                  className={`px-4 py-3.5 rounded-xl border text-left text-[14px] font-medium transition-all duration-200 flex w-full items-center justify-between gap-2 active:scale-[0.99] ${
                    formData.projectType === option.value
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-black/75 border-black/10 hover:border-black/25'
                  }`}
                >
                  <span>{option.label}</span>
                  <span className={formData.projectType === option.value ? 'text-white/80' : 'text-black/40'}>
                    {option.price}
                  </span>
                </button>
              ))}
            </div>

            {selectedOption?.hasSubOptions && (
              <div className="space-y-2 pt-1">
                <p className="text-xs font-semibold text-black/45 ml-1">{t('contact.durationLabel')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {selectedOption.subOptions.map((subOpt) => (
                    <button
                      key={subOpt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, projectSubOption: subOpt.value })}
                      className={`px-3 py-2.5 rounded-xl border text-sm text-left transition-all duration-200 active:scale-[0.99] ${
                        formData.projectSubOption === subOpt.value
                          ? 'border-black bg-black text-white'
                          : 'border-black/10 bg-white text-black/70 hover:border-black/25'
                      }`}
                    >
                      {subOpt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedOption?.hasInput && (
              <div className="pt-1">
                <Input
                  type="text"
                  value={formData.customDetails}
                  onChange={(e) => setFormData({ ...formData, customDetails: e.target.value })}
                  placeholder={t('contact.customPlaceholder')}
                  className="w-full h-12 px-4 bg-white border border-black/10 rounded-xl text-[15px] text-black placeholder:text-black/35 shadow-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/5 focus-visible:ring-offset-0 focus-visible:border-black/40 transition-all duration-200"
                  required
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-semibold text-black/55 ml-1">{t('contact.visionLabel')}</label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder={t('contact.visionPlaceholder')}
              rows={5}
              required
              className="w-full px-4 py-3.5 bg-white border border-black/10 rounded-xl text-[15px] text-black placeholder:text-black/35 shadow-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/5 focus-visible:ring-offset-0 focus-visible:border-black/40 transition-all duration-200 resize-none min-h-[120px]"
            />
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="group w-full h-14 rounded-full bg-black text-white font-medium text-[15px] shadow-none transition-all duration-300 hover:bg-black/90 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="flex items-center justify-center gap-2">
                {isSubmitting ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <Send size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                )}
                {t('contact.submit')}
              </span>
            </Button>

            <p className="text-center text-[13px] text-black/40 font-normal mt-4">
              {t('contact.guarantee')}
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;